<template lang="pug">
    div
        v-layout.align-items-end.mt-2(raw wrap)
            v-flex(xs12)

                // Back button
                back-component.ml-0.pl-0(:route="routerData")

            v-flex(xs12)

                // Session theme
                div.sessionTheme {{ sessionTheme }}

                // Session title
                title-component(:title="session.title")

        v-layout(v-if="videoOptions" raw wrap)
            v-flex(xs12)
                div.videoWrapper

                    div
                        div.videoWrapperInner
                            // Video Player
                            video-player-component(
                                ref="videoPlayer"
                                id="videoPlayer"
                                class="videoPlayer"
                                v-show="showVideo"
                                :options="videoOptions"
                                :onPlayerReady="onPlayerReady"
                            )

                            // Coming Soon
                            div.comingSoonWrapper(v-show="!showVideo && !showMood")
                                div.comingSoonBackground
                                    div.comingSoon
                                        div.title {{ $t('sessions.edit.comingSoon') }}

                    session-info(:session="session")

        v-dialog.fixedDialog(
            v-model="showMood"
            persistent
            no-click-animation
        )

            new-mood(v-if="showMood")
</template>

<script>
    import ButtonBack from '~components/ButtonBack';
    import Separator from '~components/Separator';
    import Status from '~components/Status';
    import Title from '~components/Title';
    import VideoPlayer from '~components/VideoPlayer';
    import NewMood from '~components/NewMood';
    import ToggleFullscreen from '~components/ToggleFullscreen';
    import VueTypes from 'vue-types';
    import dayjs from 'dayjs';
    import { mapActions, mapGetters } from 'vuex';
    import { defineTrackviewOptions } from '~services';
    import TrackView from '~services/TrackView';
    import SessionInfo from './SessionInfo';
    import { ids } from './NewMood/tempIds';
    import viewingTimeMixin from '~mixins/viewingTime.mixin';
    import { sessions } from '~api';

    const SKIP_MOOD_AFTER_TIMEOUT_LENGTH = 10 * 60 * 1000; // 10 minutes

    export default {
        components: {
            NewMood,
            SessionInfo,
            VideoPlayerComponent: VideoPlayer,
            ToggleComponent: ToggleFullscreen,
            TitleComponent: Title,
            StatusComponent: Status,
            SeparatorComponent: Separator,
            BackComponent: ButtonBack,
        },
        mixins: [viewingTimeMixin],
        props: {
            session: VueTypes.object.def({}),
        },
        data() {
            return {
                ids,
                fullscreen: false,
                player: null,
                showVideo: false,
                routeInitialized: false,
                timeout: null,
                moodAfterStatus: false,
                endedVideo: false,
                skipMoodAfterTimeout: null,
                videoOptions: null,
                checkVideoInterval: null,
                isFinished: false,
            };
        },
        computed: {
            ...mapGetters('sessions', [
                'showMood',
                'mood',
                'moodBefore',
                'isVoted',
                'isCongratulationDialog',
                'isChallenge'
            ]),
            ...mapGetters('auth', ['user', 'isMember', 'isAdmin', 'isGoldman']),

            recordedVideo() {
                return this.session.type === 'recorded';
            },

            /**
             * Get milliseconds that left
             * @returns {number}
             */
            time() {
                let currentDate = dayjs(new Date());
                let startedAt = dayjs(new Date(this.session.startedAt));
                let diffTime = startedAt.diff(currentDate, 'milliseconds');

                if (diffTime > 0) {
                    return diffTime;
                }

                return 0;
            },


            /**
             * Full data for router to back button
             */
            routerData() {
                if (this.session.theme) {
                    return {
                        name: 'dashboard.sessions',
                        params: {
                            theme: this.session.theme,
                            title: this.sessionTheme
                        }
                    };
                }

                return {
                    name: 'dashboard.sessions'
                };
            },

            sessionTheme() {
                return this.isFinished ? 'Recorded' : 'Live';
            }
        },
        watch: {
            showMood(val) {
                const el = document.body;

                const trackOptions = defineTrackviewOptions(
                    this.user,
                    this.session
                );

                if (val && !this.moodBefore && !this.isVoted && this.isMember) {
                    TrackView('show-mood-before', trackOptions);
                }

                if (val && this.moodBefore && this.isMember) {
                    TrackView('show-mood-after', trackOptions);
                }

                if (val) {
                    el.classList.add('modalOpen');
                    this.disableScroll();
                } else {
                    el.classList.remove('modalOpen');
                    this.enableScroll();
                }

                if (
                    !this.showMood &&
                    !this.moodAfterStatus &&
                    !self.endedVideo &&
                    this.player
                ) {
                    this.player.api('play');
                    this.player.api('unmute');
                    this.player.api('volume', 0.8);
                }

                this.player.api('seek', 300);
            }
        },
        created() {
            this.getVideoSession();
        },
        mounted() {
            this.updateWatchTimeInterval = setInterval(
                () => this.updateViewSession(),
                40000
            );
        },
        beforeDestroy() {
            if (this.player) {
                this.player.api('pause');
                this.player.api('stop');
            }

            if (this.skipMoodAfterTimeout) {
                clearTimeout(this.skipMoodAfterTimeout);
            }
        },
        methods: {
            ...mapActions('sessions', [
                'setShowMood',
                'skipMood',
                'setMoodBefore',
                'saveViewedTime',
                'setBackLink',
                'setCongratulationDialog'
            ]),

            /**
             * Show mood scale  if user wants leave page
             */
            checkLeaveRoute() {
                const sessionDuration =
                    this.session?.type === 'recorded'
                        ? this.session?.duration / 2
                        : 600;

                if (
                    this.totalSeconds >= sessionDuration &&
                    !this.moodAfterStatus
                ) {
                    this.player.api('stop');
                    this.setShowMood();
                    this.defineBackLink();
                    return false;
                }
                return true;
            },

            /**
             * Handle change fullscreen
             */
            fullscreenChange(fullscreen) {
                this.fullscreen = fullscreen;
            },

            /**
             * Close mood scale
             */
            closeMood(val) {
                this.setShowMood(this.session.type);

                // Save mood
                if (val >= 0) {
                    this.setMoodBefore({
                        id: this.session.id,
                        mood: val
                    });
                }
            },
            checkShowVideo() {
                const currentDate = dayjs(new Date());
                const startedAt = dayjs(new Date(this.session.startedAt));
                const diffTime = startedAt.diff(currentDate, 'seconds');
                const showVideo = !(diffTime > 0);
                if (showVideo) clearTimeout(this.timeoutSuggestedSession);
                this.showVideo = showVideo; // (diffTime > 0 && diffTime <= 10 * 60)
            },

            /**
             * Init player
             * @param player
             */
            onPlayerReady(player) {
                let self = this;
                let playerElement;

                // Set player
                this.player = player;

                // If no mood set autoplay in player
                if (!this.showMood && !this.moodAfterStatus) {
                    if (!self.endedVideo) {
                        this.player.api('play');
                        this.player.api('unmute');
                        this.player.api('volume', 0.8);

                        setTimeout(() => {
                            this.player.api('seek', this.videoOptions.start);
                        }, 1000);
                    }
                }

                playerElement = document.getElementById('videoPlayer');

                if (playerElement) {
                    // Play
                    playerElement.addEventListener('play', async () => {
                        await this.saveViewSession();
                        this.initUpdateViewingTime();
                    });


                    // Pause
                    playerElement.addEventListener('pause', () => {
                        this.stopTimer();
                    });

                    // Handler when FINISHED video or stream
                    playerElement.addEventListener('finish', () => {
                        this.endVideo(playerElement);
                    });
                }
            },

            endVideo(playerElement) {
                this.endedVideo = true;

                if (
                    document.fullscreenElement ||
                    document.webkitFullscreenElement ||
                    document.mozFullScreenElement
                ) {
                    document.exitFullscreen();
                }

                if (this.player) this.player.api('exitfullscreen');

                // CHECK mood status, if TRUE then show mood scale
                if (
                    this.session.todayVote &&
                    this.session.todayVote.rateBefore !== undefined &&
                    this.session.todayVote.rateBefore !== null &&
                    !this.session.todayVote.isSkip &&
                    !this.session.todayVote.isCompleted
                ) {
                    this.player.api('stop');
                    this.moodAfterStatus = true;
                    // Remove player from DOM
                    playerElement.remove();

                    this.setShowMood();
                    this.skipMoodAfterTimeout = setTimeout(() => {
                        this.skipMoodAfterTimeout = null;
                        this.skipMood();
                    }, SKIP_MOOD_AFTER_TIMEOUT_LENGTH);
                    return;
                }


                this.$router.push({ name: 'dashboard.themes' });
            },

            preventDefault(e) {
                e.preventDefault();
            },

            disableScroll() {
                document.body.addEventListener(
                    'touchmove',
                    this.preventDefault,
                    {
                        passive: false
                    }
                );
            },

            enableScroll() {
                document.body.removeEventListener(
                    'touchmove',
                    this.preventDefault
                );
            },

            async getVideoSession() {
                if (this.session.awsData) {


                    const videoSession = this.session.awsData;

                    const currentDate = dayjs();
                    const startedAt = dayjs(this.session.startedAt);
                    const diffTime = currentDate.diff(startedAt, 'seconds');
                    const isFinished = diffTime > videoSession.duration;

                    const file =
                        videoSession?.aws_data?.upstreamChannel?.outputs?.length > 0
                            ? videoSession?.aws_data?.upstreamChannel?.outputs[0]?.url
                            : null;

                    const options = {
                        file,
                        live: !isFinished
                    };

                    if (!isFinished) {
                        options.start = diffTime ?? 0;
                    }
                    if (isFinished && videoSession?.startTime)
                        options.start = videoSession?.startTime;
                    if (isFinished && videoSession?.endTime)
                        options.end = videoSession?.endTime;

                    this.videoOptions = options;


                    this.checkVideoInterval = setInterval(() => {
                        this.checkShowVideo();
                    }, 5000);
                    this.checkShowVideo();
                    this.isFinished = isFinished;
                }
            }
        }
    };
</script>

<style lang="stylus" scoped>
    @import '../../../../assets/style/_variables.styl'
    @import "../../../../assets/style/_mixins.styl"

    .videoWrapper
        position: relative
        border-radius: 10px
        overflow: hidden

    .sessionHeader
        display: flex
        align-items: center
        flex-direction: row
        margin-bottom: 10px

    .sessionTheme
        font-size: remify(20px)
        font-weight: 300

    .comingSoonWrapper
        display: flex
        background-color: $theme.black

        .comingSoonBackground
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%
            height: 100%
            min-height: 400px
            margin: 0
            background-image: url('./images/coming-soon.png')
            background-size: cover
            background-repeat: round

            .comingSoon
                text-align: center

                .title
                    font-size: 30px !important
                    font-weight: bold

    .videoWrapperInner
        position: relative
        width: 100%

    #videoAndChat
        display: flex
        width: 100%
        justify-content: center
        align-items: center

    .videoPlayer
        height: 522px
        width: 100%

        &.fullScreenVideo
            height: 100vh

    @media screen and (min-width: $grid-breakpoints.xl)
        .videoPlayer
            height: 800px

    @media screen and (max-width: $grid-breakpoints.lg - 1)
        .videoPlayer
            height: 387px

    @media screen and (max-width: $grid-breakpoints.md - 1)
        .videoPlayer
            height: 460px

        .sessionHeader
            flex-direction: column
            align-items: flex-start

        .statusWrapper
            margin-top: 5px
            order: 2

        .comingSoonWrapper
            .comingSoonBackground
                margin: 0

                .comingSoon
                    .title
                        font-size 26px !important

    @media screen and (max-width: $grid-breakpoints.md - 1)
        .videoPlayer
            height: 340px
</style>
